/**
 * broadcastAdapter.ts — Thin wrapper around BroadcastChannel.
 *
 * Uses BroadcastChannel where available (Chrome/Firefox/Safari ≥ 15.4).
 * Falls back to a no-op implementation for unsupported browsers and SSR.
 */

export interface SyncChannel {
  postMessage(data: unknown): void;
  /** Subscribe to incoming messages. Returns an unsubscribe function. */
  onMessage(handler: (data: unknown) => void): () => void;
  close(): void;
}

/** No-op fallback used in SSR or unsupported browsers. */
const noopChannel: SyncChannel = {
  postMessage: () => undefined,
  onMessage: () => () => undefined,
  close: () => undefined,
};

/**
 * Create a cross-tab sync channel.
 * Uses BroadcastChannel where available, falls back to no-op.
 */
export function createSyncChannel(name: string): SyncChannel {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return noopChannel;
  }

  const channel = new BroadcastChannel(name);

  return {
    postMessage(data: unknown): void {
      channel.postMessage(data);
    },

    onMessage(handler: (data: unknown) => void): () => void {
      const listener = (event: MessageEvent): void => {
        handler(event.data);
      };
      channel.addEventListener("message", listener);
      return () => channel.removeEventListener("message", listener);
    },

    close(): void {
      channel.close();
    },
  };
}
